import AddIcon from '@atlaskit/icon/core/add';
import type { FileIdentifier } from '@atlaskit/media-client';
import { Box, Flex, Text, xcss } from '@atlaskit/primitives';
// FIXME: circular dependency
import AttachmentViewer from '@databases/attachments/AttachmentViewer';
import { usePasteListener } from '@databases/copy-paste/usePasteListener';
import { messages } from '@databases/intl/messages';
import { MediaFormat } from '@databases/model/field';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectSelectedValuesCount,
  selectIsValueSelected,
} from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import {
  isTemplateImageCover,
  getTemplateImageIdentifier,
} from '@databases/templates/utils';
import {
  DatabaseFieldType,
  type RawDatabaseValue,
  type DatabaseAttachment,
  type DatabaseValue,
} from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  type ValueChange,
  MultiValueChangeType,
} from '@databases/utils/database/changesets';
import React, {
  type FunctionComponent,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { useIntl } from 'react-intl-next';

import { useAttachmentPaste } from '../hooks/use-attachment-paste';
import { useAttachmentUpload } from '../hooks/use-attachment-upload';
import { useMediaCollectionId } from '../hooks/use-media-collection-id';

import { type ViewComponentProps } from './types';

type Value = RawDatabaseValue<DatabaseFieldType.ATTACHMENT>[0];
const ContainerStyles = xcss({
  listStyle: 'none',
  padding: '0',
  width: '100%',
});

const ThumbnailViewContainerStyles = xcss({
  display: 'flex',
  flexWrap: 'wrap',
});

const ThumbnailViewItemStyles = xcss({
  marginRight: 'space.100',
  marginTop: '0',
  marginBottom: 'space.100',
});

const AttachmentValueViewStyles = xcss({
  position: 'relative',
});

interface AttachmentValueType extends ViewComponentProps<DatabaseFieldType.ATTACHMENT> {
  handleChange?: (
    value:
      | DatabaseAttachment
      | File
      | {
          src: string;
        }
      | {
          fileId: string;
        }
      | undefined,
    change: ValueChange | null
  ) => void;
  onReplace?: () => void;
}

const AttachmentValueView: FunctionComponent<AttachmentValueType> = ({
  contentId,
  value,
  showPlaceholder,
  field,
  entryId,
}) => {
  const { formatMessage } = useIntl();
  const dispatch = defaultStoreUtils.useDispatch();
  const mediaFormat = field.settings.mediaFormat as MediaFormat;
  const [isThumbnail, setIsThumbnail] = useState(mediaFormat === MediaFormat.THUMBNAIL);
  const arrayValue = value;

  const valueIsTheOnlyValueSelected = defaultStoreUtils.useEqualSelector((state) => {
    if (selectSelectedValuesCount(state, { contentId }) === 1 && entryId) {
      return selectIsValueSelected(state, { fieldId: field.id, entryId });
    }
    return false;
  });
  const { mediaCollectionId } = useMediaCollectionId(contentId);

  const onChange = (change: ValueChange | null) => {
    dispatch(
      addChangeToDraft(contentId, {
        type: DatabaseChangeType.SET_VALUE,
        fieldId: field.id,
        entryId: entryId || '',
        value: {
          type: DatabaseFieldType.ATTACHMENT,
          value: arrayValue,
        } as DatabaseValue,
        change,
      })
    );
  };

  const onItemChange = (value: Value, index: number) => {
    onChange({
      type: DatabaseFieldType.ATTACHMENT,
      delta: {
        type: MultiValueChangeType.EDIT,
        value,
        index,
      },
    });
  };

  const onItemAdd = (value: Value) => {
    onChange({
      type: DatabaseFieldType.ATTACHMENT,
      delta: {
        type: MultiValueChangeType.APPEND,
        value: value,
      },
    });
  };

  const handleChangeFile = (file: Value, index: number) => {
    onItemChange(file, index);
  };

  const handleAddFile = (file: Value) => {
    onItemAdd(file);
  };

  const { handleDroppedFiles } = useAttachmentUpload({
    contentId,
    arrayValue,
    mediaCollectionId,
    handleAddFile,
    handleChangeFile,
  });

  const uploadAttachment = useCallback(
    (firstImageFile: File) => {
      handleDroppedFiles([firstImageFile]);
    },
    [handleDroppedFiles]
  );

  const handleDroppedFilesWrapper = useCallback(
    (files: File[]) => {
      files.forEach((file) => uploadAttachment(file));
    },
    [uploadAttachment]
  );

  const { handlePaste } = useAttachmentPaste({
    valueIsTheOnlyValueSelected,
    handleDroppedFiles: handleDroppedFilesWrapper,
  });

  usePasteListener({
    onPaste: handlePaste,
  });

  useEffect(() => {
    setIsThumbnail(mediaFormat === MediaFormat.THUMBNAIL);
  }, [mediaFormat]);

  const mediaFiles = useMemo(() => {
    return value.map((file) => {
      if ('fileId' in file && isTemplateImageCover(file.fileId)) {
        return getTemplateImageIdentifier(file.fileId);
      }

      return {
        mediaItemType: 'file',
        id: 'fileId' in file ? file.fileId : '',
        collectionName: mediaCollectionId,
      } as FileIdentifier;
    });
  }, [value, mediaCollectionId]);

  if (!value || !value.length) {
    return (
      showPlaceholder && (
        <Flex
          xcss={xcss({
            justifyContent: 'space-between',
            alignItems: 'center',
            color: 'color.text.subtle',
            opacity: 0,
            width: '152px',
          })}
        >
          <AddIcon spacing="spacious" label="" />
          <Text color={'color.text.subtle'}>
            {formatMessage(messages['media.add-file'])}
          </Text>
        </Flex>
      )
    );
  }

  return (
    <Box xcss={[ContainerStyles, isThumbnail && ThumbnailViewContainerStyles]} as="ul">
      {value.map((v) => (
        <Box
          as="li"
          key={`${v._id}`}
          xcss={[AttachmentValueViewStyles, isThumbnail && ThumbnailViewItemStyles]}
        >
          <AttachmentViewer
            contentId={contentId}
            value={v}
            mediaFormat={mediaFormat}
            attachmentLocation="view"
            mediaFiles={mediaFiles}
          />
        </Box>
      ))}
    </Box>
  );
};

export default AttachmentValueView;
